import { AddProductSchema } from '../components/AddProductContent';

export interface AddProductPayload {
  productName: string;
  description: string;
  categoryId: string;
  specialityId?: string;
  hsCodeId?: string;
  productImages: {
    id: string;
    isThumbnail: boolean;
  }[];
  logoImageId: string | null;
  minQty: number;
  leadTime: number;
  locationId?: string;
  priceType: 'CART' | 'RFQ_METHOD';
  applyVariants: boolean;
  productPrice: number | null;
  stock: number | null;
  variants:
  | {
    sku: string | null;
    stock: number;
    price?: number;
    attributes: {
      categoryAttributeId: string;
      categoryAttributeDetailId: string | null;
      colorImageId: string | null;
      nameEn: string | null;
      nameAr: string | null;
    }[];
  }[]
  | null;
  applyPricingTiers: boolean;
  pricingTiers:
  | {
    minQty: number;
    maxQty?: number;
    pricePerUnit: number;
    internalLabel?: string;
  }[]
  | null;
  specs: {
    specType: string;
    specValue: string;
  }[];
  itemPerCarton?: number;
  grossWeight?: number;
  height?: number;
  width?: number;
  length?: number;
  acceptSampleOrder: boolean;
  minSampleQty: number | null;
  samplePrice: number | null;
  certificatesDocs: { name: string; id: string }[];
  customizationTypes: string[];
}

export const buildAddProductPayload = (data: AddProductSchema): AddProductPayload => {
  // 1. Base Info (Shared across all scenarios)
  const payload: AddProductPayload = {
    productName: data.productName,
    description: data.description,
    categoryId: data.categoryId,
    specialityId: data.specialityId,
    hsCodeId: data.hsCodeId || undefined,
    productImages: data.productImages.map((img, index) => ({
      id: img.id,
      isThumbnail: index === 0,
    })),
    logoImageId: data.customLogo?.[0]?.uid ?? null,
    minQty: data.minQty!,
    leadTime: data.leadTime!,
    locationId: data.locationId,
    priceType: data.productType === 'cart' ? 'CART' : 'RFQ_METHOD',
    applyVariants: data.applyVariants,
    applyPricingTiers: data.applyPricingTiers,
    productPrice: null,
    stock: null,
    variants: null,
    pricingTiers: null,
    specs: data.specs.map((s) => ({ specType: s.type, specValue: s.value })),
    itemPerCarton: data.itemPerCarton,
    grossWeight: data.grossWeight,
    height: data.height,
    width: data.width,
    length: data.length,
    acceptSampleOrder: data.acceptSampleOrder,
    minSampleQty: data.acceptSampleOrder ? (data.minSampleQty ?? null) : null,
    samplePrice: data.acceptSampleOrder ? (data.samplePrice ?? null) : null,
    certificatesDocs: data.certificatesDocuments?.map((d) => ({ name: d.certificateName, id: d.uid })) ?? [],
    customizationTypes: data.customizationTypes ?? [],
  };

  const isCart = data.productType === 'cart';
  const isRfq = data.productType === 'rfq';

  // 2. Variants Mapping (Scenarios 1, 3, 4)
  if (data.applyVariants) {
    // Include price in variants for Cart, OR for RFQ when no pricing tiers exist
    const includeVariantPrice = isCart || (isRfq && !data.applyPricingTiers);

    type VariantDisplayDetail = {
      attrId: string;
      detail: {
        id: string | null;
        name: string;
        nameEn?: string;
        nameAr?: string;
      };
    };

    type VariantData = {
      _displayDetails?: VariantDisplayDetail[];
      sku?: string | null;
      stock?: number;
      price?: number;
      priceConverted?: number;
    };

    payload.variants = (data.variants || []).map((variantData: VariantData) => {
      const attributesPayload = variantData._displayDetails?.map((d: VariantDisplayDetail) => {
        const isColor = d.attrId === 'color';
        const isCustomColor = isColor && d.detail.id === null;

        let colorImageId = null;
        if (isColor) {
          const colorRow = data.colors?.find(
            (c) => c.color?.id === d.detail.id && c.color?.name === d.detail.name
          );
          colorImageId = colorRow?.image?.[0]?.uid ?? null;
        }

        return {
          categoryAttributeId: isColor ? (data.colorAttributeId ?? '') : d.attrId,
          categoryAttributeDetailId: d.detail.id,
          colorImageId: colorImageId,
          nameEn: isCustomColor ? d.detail.name : null,
          nameAr: isCustomColor ? d.detail.name : null,
        };
      }) || [];

      const variant: NonNullable<AddProductPayload['variants']>[number] = {
        sku: variantData.sku ?? null,
        stock: Number(variantData.stock),
        attributes: attributesPayload,
      };

      if (includeVariantPrice) {
        variant.price = Number(variantData.price) || 0;
      }

      return variant;
    });
  } else {
    // SCENARIO 2 & 5: Without variants
    payload.stock = data.baseStock ?? null;
    if (!data.applyPricingTiers) {
      payload.productPrice = data.basePrice ?? null;
    }
  }

  // 3. Pricing Tiers Mapping (Scenario 3)
  if (isRfq && data.applyPricingTiers) {
    payload.pricingTiers = (data.pricingTiers || []).map((t) => ({
      minQty: t.minQty!,
      maxQty: t.maxQty,
      pricePerUnit: t.pricePerUnit!,
      internalLabel: t.internalLabel,
    }));
  }

  return payload;
};
